import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { useEffect, useState } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import { connect } from 'react-redux';
import { FontIcon, Spinner, TooltipHost, DirectionalHint } from '@fluentui/react';
import moment from 'moment';

//functions
import * as ComponentFunctions from './SettlementResumeList.Functions';

//styles
import './SettlementResumeList.Styles.css';

// components
import SettlementSummaryItem from '../SettlementSummaryItem/SettlementSummaryItem.Component';
import SettlementDetailsListComponet from '../SettlementDetailsList/SettlementDetailsList.Component';
import SettlementResumeFormComponent from '../SettlementResumeForm/SettlementResumeForm.Component';
import SettlementDetailForm from '../SettlementDetailForm/SettlementDetailForm.Component';

//i18Next
import { useTranslation } from 'react-i18next';
import { SettlementResume } from '../../../models/Entities/SettlementResume/SettlementResume';
import { SettlementDetail } from '../../../models/Entities/SettlementDetails/SettlementDetail';

export const SettlementResumeListComponent = (props: ComponentFunctions.Props) => {

  const [selectedResumeId, setSelectedResumeId] = useState<number | undefined>(undefined);
  const [showDetailsForm, setShowDetailsForm] = useState(false);
  const { t } = useTranslation();

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [isOpenResumeForm, { setTrue: openPanelResumeForm, setFalse: dismissPanelResumeForm }] = useBoolean(false);
  const [isOpenConceptForm, { setTrue: openPanelConceptForm, setFalse: dismissPanelConceptForm }] = useBoolean(false);

  useEffect(() => {
    props.GetSettlementById(props.SettlementId);
  }, []);

  // Los resúmenes se piden una vez que la liquidación está en el store y no se
  // pidieron todavía. Antes se miraba si la lista estaba vacía, y una respuesta
  // tardía del GET de la liquidación podía dejarla vacía sin volver a pedirlos.
  const resumesRequested = props.Settlement?.isGettingResume || props.Settlement?.isGettingResumeSuccess !== undefined;
  useEffect(() => {
    if (props.Settlement && !resumesRequested)
      props.GetAllSettlementResume(props.SettlementId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.Settlement?.id, resumesRequested]);

  const handleClosePanel = () => { setSelectedResumeId(undefined); dismissPanel(); };
  const handleCloseResumeForm = () => { setSelectedResumeId(undefined); dismissPanelResumeForm(); };
  const handleCloseConceptForm = () => { setShowDetailsForm(false); dismissPanelConceptForm(); };

  // ── helpers ──────────────────────────────────────────────────────────────

  const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);

  const pct = (pctValue: number, amount: number, currency: string) =>
    <><span className="srl-pct">({pctValue}%)</span>&nbsp;{fmt(amount, currency)}</>;

  const buildACobrarTooltip = (detail: SettlementDetail) => (
    <div className="srl-cobrar-tooltip">
      <div className="srl-cobrar-tooltip-title">Cómo se calcula el importe a cobrar</div>
      <div className="srl-cobrar-tooltip-row">
        <span>Honorarios centro médico</span>
        <span>{fmt(detail.ownerAmount, detail.currency)}</span>
      </div>
      <div className="srl-cobrar-tooltip-row srl-cobrar-tooltip-deduct">
        <span>− Retención centro ({detail.withHolingPercentage}%)</span>
        <span>− {fmt(detail.ownerAmountWithholding, detail.currency)}</span>
      </div>
      <div className="srl-cobrar-tooltip-row srl-cobrar-tooltip-result">
        <span>Total a cobrar</span>
        <span>{fmt(detail.ownerAmountWithholdingTotal, detail.currency)}</span>
      </div>
    </div>
  );

  // ── sub-renders ───────────────────────────────────────────────────────────

  const renderDetailRow = (detail: SettlementDetail) => (
    <tr key={detail.id} className="srl-detail-row">
      <td>
        <span className="srl-patient-chip">{detail.patientNumberInProtocol}</span>
      </td>
      <td className="srl-date">
        {detail.dueDate ? moment(detail.dueDate).format('DD/MM/YYYY') : '—'}
      </td>
      <td className="srl-concept">
        <strong>{detail.conceptName}</strong>
        {detail.contractStageName && (
          <span className="srl-stage-badge">{detail.contractStageName}</span>
        )}
      </td>
      <td className="srl-amount">{fmt(detail.amount, detail.currency)}</td>
      <td className="srl-withholding srl-amount--ret">{fmt(detail.ownerAmountWithholding, detail.currency)}</td>
      <td className="srl-withholding srl-amount--ret">{fmt(detail.teamAmountWithholding, detail.currency)}</td>
      <td className="srl-fee">{pct(detail.feeDivisionPercentage, detail.ownerAmount, detail.currency)}</td>
      <td className="srl-fee">{pct(detail.teamDivisionPercentage, detail.teamAmount, detail.currency)}</td>
      <td className="srl-amount srl-amount--highlight">
        <TooltipHost
          content={buildACobrarTooltip(detail)}
          directionalHint={DirectionalHint.bottomRightEdge}
          calloutProps={{ gapSpace: 4, beakWidth: 8 }}
        >
          <span className="srl-cobrar-value">
            {fmt(detail.ownerAmountWithholdingTotal, detail.currency)}
          </span>
        </TooltipHost>
      </td>
    </tr>
  );

  const renderDetailsTable = (details: SettlementDetail[]) => {
    const grandByCurrency: Record<string, {
      bruto: number; retCentro: number; retEquipo: number;
      honorCentro: number; honorEquipo: number; aCobrar: number;
    }> = {};
    details.forEach((d) => {
      const cur = (d.currency || 'ARS').toUpperCase();
      if (!grandByCurrency[cur]) grandByCurrency[cur] = { bruto: 0, retCentro: 0, retEquipo: 0, honorCentro: 0, honorEquipo: 0, aCobrar: 0 };
      grandByCurrency[cur].bruto       += d.amount                       || 0;
      grandByCurrency[cur].retCentro   += d.ownerAmountWithholding        || 0;
      grandByCurrency[cur].retEquipo   += d.teamAmountWithholding         || 0;
      grandByCurrency[cur].honorCentro += d.ownerAmount                   || 0;
      grandByCurrency[cur].honorEquipo += d.teamAmount                    || 0;
      grandByCurrency[cur].aCobrar     += d.ownerAmountWithholdingTotal   || 0;
    });

    return (
      <div className="srl-table-wrapper">
        <table className="srl-table">
          <thead>
            <tr className="srl-thead-group">
              <th rowSpan={2}>Paciente</th>
              <th rowSpan={2}>Fecha</th>
              <th rowSpan={2}>Concepto</th>
              <th rowSpan={2}>Total bruto</th>
              <th colSpan={2} className="srl-th-group srl-th-ret">Retención</th>
              <th colSpan={2} className="srl-th-group">División de honorarios</th>
              <th rowSpan={2} className="srl-th-collect">A cobrar</th>
            </tr>
            <tr className="srl-thead-sub">
              <th className="srl-th-ret">Centro</th>
              <th className="srl-th-ret">Equipo</th>
              <th>Centro</th>
              <th>Equipo médico</th>
            </tr>
          </thead>
          <tbody>
            {details.map(renderDetailRow)}
          </tbody>
          {Object.entries(grandByCurrency).map(([cur, g]) => (
            <tfoot key={`grand-${cur}`}>
              <tr className="srl-grand-total">
                <td colSpan={3} className="srl-grand-label">
                  Total general{Object.keys(grandByCurrency).length > 1 ? ` ${cur}` : ''}
                </td>
                <td className="srl-amount srl-grand-amount">{fmt(g.bruto, cur)}</td>
                <td className="srl-amount srl-grand-amount srl-amount--ret">{fmt(g.retCentro, cur)}</td>
                <td className="srl-amount srl-grand-amount srl-amount--ret">{fmt(g.retEquipo, cur)}</td>
                <td className="srl-amount srl-grand-amount">{fmt(g.honorCentro, cur)}</td>
                <td className="srl-amount srl-grand-amount">{fmt(g.honorEquipo, cur)}</td>
                <td className="srl-amount srl-grand-amount srl-amount--highlight srl-grand-collect">
                  {fmt(g.aCobrar, cur)}
                </td>
              </tr>
            </tfoot>
          ))}
        </table>
      </div>
    );
  };

  const renderDetailsFooter = (details: SettlementDetail[]) => {
    const byCurrency: Record<string, { bruto: number; ownerAmount: number; teamAmount: number; retencionTotal: number; retencionCentro: number; retencionEquipo: number; aCobrar: number }> = {};
    details.forEach((d) => {
      const cur = (d.currency || 'ARS').toUpperCase();
      if (!byCurrency[cur]) byCurrency[cur] = { bruto: 0, ownerAmount: 0, teamAmount: 0, retencionTotal: 0, retencionCentro: 0, retencionEquipo: 0, aCobrar: 0 };
      byCurrency[cur].bruto           += d.amount                       || 0;
      byCurrency[cur].ownerAmount     += d.ownerAmount                   || 0;
      byCurrency[cur].teamAmount      += d.teamAmount                    || 0;
      byCurrency[cur].retencionTotal  += d.withHolingAmount              || 0;
      byCurrency[cur].retencionCentro += d.ownerAmountWithholding        || 0;
      byCurrency[cur].retencionEquipo += d.teamAmountWithholding         || 0;
      byCurrency[cur].aCobrar         += d.ownerAmountWithholdingTotal   || 0;
    });
    return (
      <div className="srl-oc-footer">
        {Object.entries(byCurrency).map(([cur, t]) => {
          const totalACobrar = t.aCobrar;
          return (
            <div key={cur} className="srl-oc-totals">
              <div className="srl-oc-currency">{cur}</div>
              <table className="srl-oc-table">
                <tbody>

                  {/* ── Composición del total a liquidar ───── */}
                  <tr className="srl-oc-bruto">
                    <td className="srl-oc-label">Total a liquidar</td>
                    <td className="srl-oc-amount">{fmt(t.bruto, cur)}</td>
                  </tr>
                  <tr className="srl-oc-sub">
                    <td className="srl-oc-label">Honorarios centro médico</td>
                    <td className="srl-oc-amount">{fmt(t.ownerAmount, cur)}</td>
                  </tr>
                  <tr className="srl-oc-sub">
                    <td className="srl-oc-label">Honorarios equipo médico</td>
                    <td className="srl-oc-amount">{fmt(t.teamAmount, cur)}</td>
                  </tr>

                  {/* ── Composición de la retención ────────── */}
                  <tr className="srl-oc-retention">
                    <td className="srl-oc-label">Retención total</td>
                    <td className="srl-oc-amount">{fmt(t.retencionTotal, cur)}</td>
                  </tr>
                  <tr className="srl-oc-sub">
                    <td className="srl-oc-label">Retención centro médico</td>
                    <td className="srl-oc-amount">{fmt(t.retencionCentro, cur)}</td>
                  </tr>
                  <tr className="srl-oc-sub">
                    <td className="srl-oc-label">Retención equipo médico</td>
                    <td className="srl-oc-amount">{fmt(t.retencionEquipo, cur)}</td>
                  </tr>

                  {/* ── Deducciones para total a cobrar ─────── */}
                  <tr className="srl-oc-deduct srl-oc-deduct-first">
                    <td className="srl-oc-label">Honorarios equipo médico</td>
                    <td className="srl-oc-amount srl-oc-neg">− {fmt(t.teamAmount, cur)}</td>
                  </tr>
                  <tr className="srl-oc-deduct">
                    <td className="srl-oc-label">Retención centro médico</td>
                    <td className="srl-oc-amount srl-oc-neg">− {fmt(t.retencionCentro, cur)}</td>
                  </tr>

                  {/* ── Total a cobrar ───────────────────── */}
                  <tr className="srl-oc-total">
                    <td className="srl-oc-label">Total a cobrar</td>
                    <td className="srl-oc-amount srl-oc-pos">{fmt(totalACobrar, cur)}</td>
                  </tr>

                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  // ── modals ────────────────────────────────────────────────────────────────

  const Modals = (
    <>
      <SettlementDetailsListComponet
        isOpen={isOpen}
        openPanel={openPanel}
        dismissPanel={handleClosePanel}
        SettlementId={props.SettlementId}
        SettlementResumeId={selectedResumeId}
      />
      <SettlementResumeFormComponent
        isOpen={isOpenResumeForm}
        openPanel={openPanelResumeForm}
        dismissPanel={handleCloseResumeForm}
        SettlementId={props.SettlementId}
        SettlementResumeId={selectedResumeId}
      />
      <SettlementDetailForm
        isOpen={showDetailsForm}
        dismissPanel={handleCloseConceptForm}
        openPanel={openPanelConceptForm}
        SettlementId={props.SettlementId}
        SettlementResumeId={undefined}
        SettlementDetailId={undefined}
      />
    </>
  );

  // ── render ────────────────────────────────────────────────────────────────

  if (props.isLoading) {
    return (
      <div className="srl-loading">
        <Spinner
          label={t('settlementResumeList.loading.loadingSettlementSummary')}
          ariaLive="assertive"
          labelPosition="top"
        />
      </div>
    );
  }

  const totalResumes = props.SettlementResumes?.length ?? 0;

  return (
    <div className="srl-root">

      {/* ── Section 1: resumes por visita ─────────────────────────────── */}
      {totalResumes > 0 && (
        <section className="srl-section">
          <div className="srl-section-header">
            <FontIcon iconName="CalendarAgenda" className="srl-section-icon-fluent" />
            <h2 className="srl-section-title">Resumen por visita</h2>
            <span className="srl-section-badge">{totalResumes}</span>
          </div>
          <div className="srl-resume-list">
            {(props.SettlementResumes ?? []).map((resume: SettlementResume) => (
              <SettlementSummaryItem
                key={resume.id}
                SettlementId={props.SettlementId}
                SettlementSummaryItemId={resume.id}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Section 2: otros conceptos (detalles manuales) ─────────────── */}
      {props.SettlementDetails && props.SettlementDetails.length > 0 && (
        <section className="srl-section srl-section--alt">
          <div className="srl-section-header">
            <FontIcon iconName="PageList" className="srl-section-icon-fluent" />
            <h2 className="srl-section-title">Otros conceptos</h2>
            <span className="srl-section-badge">{props.SettlementDetails.length}</span>
          </div>
          {renderDetailsTable(props.SettlementDetails)}
          {renderDetailsFooter(props.SettlementDetails)}
        </section>
      )}

      {Modals}
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(SettlementResumeListComponent as any);

